import { ChangeDetectionStrategy, Component, Input, OnInit, ViewChild, ViewEncapsulation } from "@angular/core";
import { ISplugDefinition, SplugDefinitionRemoteControllerService } from "core/api";
import { DynamicFormComponent } from "core/dynamic-form";
import { filterAllTruthyInTuple, mapKey, subjectFromObs } from "core/rx-js";
import { LoaderService } from "core/services";
import { SplugConfigService } from "core/splug";
import { DsgDeepTranslationKey } from "projects/designer/src/app/pipes/translate.pipe";
import { BehaviorSubject, Subject, combineLatest, distinctUntilChanged, filter, map, switchMap, take, takeUntil } from "rxjs";
import { ActorSplugInfoService } from "../actor-splug-info/actor-splug-info.service";


@Component({
  selector: 'ss-actor-splug-form',
  templateUrl: './actor-splug-form.component.html',
  styleUrls: ['./actor-splug-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ActorSplugFormComponent implements OnInit {

  private readonly _destroy = new Subject<void>();

  @ViewChild(DynamicFormComponent)
  private _dynamicForm!: DynamicFormComponent

  readonly dynamicFormSubject = new BehaviorSubject<DynamicFormComponent>(undefined);

  readonly splugDefinitionSubject = new BehaviorSubject<ISplugDefinition>(undefined);
  @Input() get splugDefinition() {
    return this.splugDefinitionSubject.value;
  }
  set splugDefinition(value) {
    this.splugDefinitionSubject.next(value);
  }
  get validationKey() {
    return this.splugDefinition.id + '.validate'
  }

  readonly validationResultSubject = this._actorSplugInfoService.validationResultSubject;
  readonly validationResultObs = combineLatest([
    this.splugDefinitionSubject,
    this.validationResultSubject
  ]).pipe(
    filterAllTruthyInTuple(),
    map(([splugDefinition, validationResult]) => validationResult[splugDefinition.id])
  );

  readonly validationMessageObs = this.validationResultObs.pipe(filter(Boolean), mapKey('validationErrorMessage'))
  readonly validationColorObs = this.validationResultObs.pipe(filter(Boolean), mapKey('status'), map((status) => status === 'OK' ? 'lightgreen' : 'red'))

  readonly controlsObs = this.splugDefinitionSubject.pipe(
    filter(Boolean),
    map((splugDefinition) => splugDefinition.controls),
    filter(Boolean),
  )

  readonly splugIdSubject = subjectFromObs({
    initialValue: undefined,
    until: this._destroy,
    observable: this.splugDefinitionSubject.pipe(
      filter(Boolean),
      map(({ id }) => id))

  })

  readonly selectedSplugsSubject = this._actorSplugInfoService.selectedSplugsSubject;
  readonly isSelectedSubject = subjectFromObs({
    observable: combineLatest([this.selectedSplugsSubject, this.splugIdSubject])
      .pipe(
        filterAllTruthyInTuple(),
        map(([selectedSplugs, splugId]) => selectedSplugs[splugId]),
        distinctUntilChanged()
      ),
    initialValue: false
  })


  readonly actorSplugParamsObs = this._actorSplugInfoService.actorSplugParamsObs;

  readonly initialSplugParamsSubject = subjectFromObs({
    observable: combineLatest([
      this.actorSplugParamsObs,
      this.splugDefinitionSubject]
    )
      .pipe(
        filterAllTruthyInTuple(),
        map(([actorSplugParams, splugDefinition]) => actorSplugParams.find(({ sigTypeId }) => splugDefinition.id === sigTypeId)),
        filter(Boolean),
        map((actorSplugParamsObs) => actorSplugParamsObs.sigTypeParam),
      ),
    initialValue: null,
    until: this._destroy
  })

  readonly controlsSubject = subjectFromObs({
    initialValue: undefined,
    until: this._destroy,
    observable: this.splugDefinitionSubject.pipe(
      filter(Boolean),
      map(({ controls }) => controls))
  })


  readonly mainFormGroupObs = this.dynamicFormSubject.pipe(
    filter(Boolean),
    switchMap(dynamicForm => dynamicForm.mainFormGroupSubject),
    filter(Boolean)
  );

  readonly mainFormGroupSubject = subjectFromObs({
    until: this._destroy,
    observable: this.mainFormGroupObs,
    initialValue: null
  })
  get mainFormGroup() {
    return this.mainFormGroupSubject.value;
  }

  readonly splugFormGroup = this._actorSplugInfoService.splugFormGroup;
  readonly initalSplugFormDataSubject = this._actorSplugInfoService.initalSplugFormDataSubject;


  readonly initalFormDataObs = combineLatest([
    this.splugIdSubject,
    this.initalSplugFormDataSubject
  ])
    .pipe(
      filterAllTruthyInTuple(),
      map(([splug, initialFormData]) => initialFormData[splug]),
    )


  readonly formValidWithOneFieldObs = this.mainFormGroupSubject.pipe(
    filter(Boolean),
    distinctUntilChanged(),
    switchMap((mainFormGroup) => combineLatest([
      mainFormGroup.valueChanges,
      mainFormGroup.statusChanges
    ])),
    map(([value, status]) => {

      if (!value) {
        return false;
      }
      if (Object.values(value).filter((value) => value !== null && value !== undefined).length === 0) {
        return false;
      }

      if (status !== 'VALID') {
        return false;
      }

      return true;
    }),
    distinctUntilChanged(),

  );

  get actor() {
    return this._actorSplugInfoService.data.actor
  }

  constructor(
    private readonly _actorSplugInfoService: ActorSplugInfoService,
    private readonly _splugDefinitionRemoteControllerService: SplugDefinitionRemoteControllerService,
    private readonly _loaderService: LoaderService<DsgDeepTranslationKey>,
    private readonly _splugConfigService: SplugConfigService
  ) {


    combineLatest([this._actorSplugInfoService.submittedAllSubject, this.isSelectedSubject, this.dynamicFormSubject])
      .pipe(
        filter(([, , dynamicForm]) => Boolean(dynamicForm)),
        map(([submittedAll, isSelected]) => submittedAll && isSelected),
        distinctUntilChanged(),
        takeUntil(this._destroy)
      )
      .subscribe((submitted) => {
        this._dynamicForm.submittedSubject.next(submitted);
      })

    combineLatest([this.mainFormGroupObs, this.controlsObs]).pipe(
      takeUntil(this._destroy),
      filterAllTruthyInTuple(),
      filter(() => !!this.actor?.mobile),
    )
      .subscribe(([mainFormGroup, controls]) => {

        controls
          .filter(({ type }) => type === 'phone')
          .map((ctrl) => mainFormGroup.get(ctrl.dataField))
          .forEach((ctrl) => {


            ctrl.actionSubject.next({
              callback: (control) => control.setValue(this.actor.mobile),
              color: 'primary',
              icon: 'edit',
              tooltip: 'ss-splug.add-default-mobile'
            })

          })

      })

    combineLatest([
      this.mainFormGroupObs,
      this.splugDefinitionSubject
    ])
      .pipe(
        filterAllTruthyInTuple(),
        take(1)
      )
      .subscribe(([formGroup, splugDefinition]) => {
        this._actorSplugInfoService.splugFormArray.push(formGroup)
        this.splugFormGroup[splugDefinition.id] = formGroup;
      })


    combineLatest([
      this.mainFormGroupObs,
      this.initalFormDataObs
    ])
      .pipe(
        filterAllTruthyInTuple(),
        take(1)
      )
      .subscribe(([formGroup, initialFormData]) => {

        Object
          .entries(initialFormData)
          .forEach(([dataField, value]) => {
            formGroup.get(dataField).setValue(value)
          })
      })




    this.formValidWithOneFieldObs.pipe(takeUntil(this._destroy))
      .subscribe((validWithOneField) => {
        if (validWithOneField) {
          this._actorSplugInfoService.selectSplug(this.splugDefinition.id)
          return;
        }

        this._actorSplugInfoService.deselectSplug(this.splugDefinition.id)
      })
  }

  ngOnInit(): void {

    combineLatest([
      this.initialSplugParamsSubject,
      this.mainFormGroupSubject
    ])
      .pipe(
        filterAllTruthyInTuple(),
        takeUntil(this._destroy)
      )
      .subscribe(([initialFormData, form]) => {
        form.reset();
        form.patchValue(initialFormData);
      })

  }

  ngAfterViewInit() {

    this.dynamicFormSubject.next(this._dynamicForm)


  }


  ngOnDestroy() {

    const thisIdx = this._actorSplugInfoService.splugFormArray.controls.indexOf(this.mainFormGroup!);
    this._actorSplugInfoService.splugFormArray.removeAt(thisIdx);

  }


  validate() {

    const form = this._dynamicForm.mainFormGroup;
    if (!form.valid) {
      this._dynamicForm.submittedSubject.next(true);

    }

    const id = this._loaderService.show({ key: this.validationKey })
    const keyValuePairs = Object.entries(this.mainFormGroup.value)
      .map(([key, value]) => ({
        key,
        value: this._splugConfigService.executeStringifer(this.splugDefinition.id, key, value)
      }));

    this._splugDefinitionRemoteControllerService.validateSplugParams(this.splugDefinition.id, keyValuePairs)
      .pipe(
        this._loaderService.disposeLoader(id),
        take(1)
      )
      .subscribe((validationResult) => {
        const prev = { ...this._actorSplugInfoService.validationResultSubject.value }
        this._actorSplugInfoService.validationResultSubject.next({
          ...prev,
          [this.splugDefinition.id]: validationResult
        })
      })
  }

}
